import { useState } from 'react'
import { useAuthStore } from '@/stores/auth'
import { Bell, Mail, MessageSquare, Smartphone } from 'lucide-react'

type NotificationPreferences = {
  email: {
    order_updates: boolean
    stock_alerts: boolean
    promotions: boolean
    newsletter: boolean
  }
  sms: {
    order_updates: boolean
    stock_alerts: boolean
    promotions: boolean
  }
  push: {
    order_updates: boolean
    stock_alerts: boolean
    promotions: boolean
  }
}

export default function AccountNotificationsPage() {
  const { user } = useAuthStore()
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email: {
      order_updates: true,
      stock_alerts: true,
      promotions: false,
      newsletter: false,
    },
    sms: {
      order_updates: true,
      stock_alerts: false,
      promotions: false,
    },
    push: {
      order_updates: true,
      stock_alerts: true,
      promotions: false,
    },
  })

  const updatePreference = (
    channel: 'email' | 'sms' | 'push',
    type: string,
    value: boolean
  ) => {
    setPreferences({
      ...preferences,
      [channel]: {
        ...preferences[channel],
        [type]: value,
      },
    })
  }

  if (!user) return null

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-foreground mb-2">
            Manage Notifications
          </h2>
          <p className="text-sm text-muted-foreground">
            Choose how you want to receive updates about your orders, promotions, and more
          </p>
        </div>

        {/* Email Notifications */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Email Notifications</h3>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>

          <div className="space-y-4 pl-14">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <h4 className="font-medium text-foreground">Order Updates</h4>
                <p className="text-sm text-muted-foreground">
                  Order confirmations, shipping updates, delivery notifications
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.email.order_updates}
                  onChange={(e) => updatePreference('email', 'order_updates', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <h4 className="font-medium text-foreground">Stock Alerts</h4>
                <p className="text-sm text-muted-foreground">
                  Get notified when out-of-stock items are available again
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.email.stock_alerts}
                  onChange={(e) => updatePreference('email', 'stock_alerts', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <h4 className="font-medium text-foreground">Promotions & Offers</h4>
                <p className="text-sm text-muted-foreground">
                  Special deals, discounts, and promotional campaigns
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.email.promotions}
                  onChange={(e) => updatePreference('email', 'promotions', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <h4 className="font-medium text-foreground">Newsletter</h4>
                <p className="text-sm text-muted-foreground">
                  Weekly updates, tips, and news from BotTola
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.email.newsletter}
                  onChange={(e) => updatePreference('email', 'newsletter', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>
          </div>
        </div>

        {/* SMS Notifications */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MessageSquare className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">SMS Notifications</h3>
              <p className="text-sm text-muted-foreground">{user.phone}</p>
            </div>
          </div>

          <div className="space-y-4 pl-14">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <h4 className="font-medium text-foreground">Order Updates</h4>
                <p className="text-sm text-muted-foreground">
                  Critical order updates via SMS
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.sms.order_updates}
                  onChange={(e) => updatePreference('sms', 'order_updates', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <h4 className="font-medium text-foreground">Stock Alerts</h4>
                <p className="text-sm text-muted-foreground">
                  SMS alerts for back-in-stock items
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.sms.stock_alerts}
                  onChange={(e) => updatePreference('sms', 'stock_alerts', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <h4 className="font-medium text-foreground">Promotions & Offers</h4>
                <p className="text-sm text-muted-foreground">
                  Limited time offers via SMS
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.sms.promotions}
                  onChange={(e) => updatePreference('sms', 'promotions', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Push Notifications */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Push Notifications</h3>
              <p className="text-sm text-muted-foreground">Browser and mobile app notifications</p>
            </div>
          </div>

          <div className="space-y-4 pl-14">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <h4 className="font-medium text-foreground">Order Updates</h4>
                <p className="text-sm text-muted-foreground">
                  Real-time order status updates
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.push.order_updates}
                  onChange={(e) => updatePreference('push', 'order_updates', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-border">
              <div>
                <h4 className="font-medium text-foreground">Stock Alerts</h4>
                <p className="text-sm text-muted-foreground">
                  Instant notifications when items are restocked
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.push.stock_alerts}
                  onChange={(e) => updatePreference('push', 'stock_alerts', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <h4 className="font-medium text-foreground">Promotions & Offers</h4>
                <p className="text-sm text-muted-foreground">
                  Flash sales and exclusive deals
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.push.promotions}
                  onChange={(e) => updatePreference('push', 'promotions', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Bell className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-medium text-foreground mb-1">About Notifications</h3>
            <p className="text-sm text-muted-foreground">
              You can update your notification preferences at any time. Order-related notifications are
              recommended to keep you informed about your purchases.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
